import {
  faCheckCircle,
  faFileLines,
  faFolder,
  faHomeAlt,
  faUser,
  faUserFriends,
  faUserGear,
  faUsers,
  faClock,
  faGear,
  faMemoCircleCheck,
  // faChartMixed,
  faCalendarDays,
  IconDefinition,
  faChartMixed,
} from "@fortawesome/pro-light-svg-icons";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { useLocation } from "react-router-dom";
import { IMAGES } from "src/assets";
import { useIsMobile } from "src/hooks/useIsMobile";
import { setOpenedAssetsPage, setOpenedClientsPage } from "src/redux/actions/openedPages";
import { getUserData } from "src/redux/selectors/user";
import { useTestGroup } from "src/hooks/useTestGroup";
import { FirebaseDataService, ClientPortalTab } from "src/services/Firebase/data";
import { colors } from "src/styles";
import { NavbarItem } from "./NavbarItem";
import { NavbarToggle } from "./NavbarToggle";
import { ToggleArrow } from "./ToggleArrow";

const ICON_MAP: Record<string, IconDefinition> = {
  faHomeAlt,
  faUser,
  faUserFriends,
  faUserGear,
  faUsers,
  faClock,
  faGear,
  faMemoCircleCheck,
  faFileLines,
  faFolder,
  faCalendarDays,
  faCheckCircle,
};

interface NavbarProps {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}
const Navbar: React.FC<NavbarProps> = ({ isVisible, setIsVisible }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { isMobile } = useIsMobile();

  const BROOKEFIELD_CLIENT_ID = "BROOKFIELD";

  const { isManager, email, isClient, clientId } = useSelector(getUserData);

  // null = not yet fetched (show static fallback); [] = fetched but empty (show static fallback); [...] = use dynamic tabs
  const [clientTabs, setClientTabs] = useState<ClientPortalTab[] | null>(null);
  const isTestGroup = useTestGroup(email);
  const isBrookefieldClient = (clientId ?? "").trim().toUpperCase() === BROOKEFIELD_CLIENT_ID;
  const hasDynamicTabs = isClient && clientTabs !== null && clientTabs.length > 0;
  const hasSchedulesTab =
    clientTabs?.some(
      (tab) => tab.name.trim().toLowerCase() === "schedules" || tab.route?.split("?")[0] === "/schedules",
    ) ?? false;

  useEffect(() => {
    setIsVisible(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (isClient && clientId) {
      FirebaseDataService.getClientPortalTabs(clientId)
        .then((tabs) => setClientTabs(tabs || []))
        .catch(() => setClientTabs([])); // API error → show static fallback
    } else if (isClient) {
      setClientTabs([]); // no clientId available → show static fallback
    }
  }, [isClient, clientId]);

  return (
    <div style={{ width: !isMobile ? (isVisible ? "270px" : "100px") : "0px" }}>
      <Container $isMobile={isMobile} $isVisible={isVisible}>
        <NavbarToggle $isMobile={isMobile} $isVisible={isVisible} setIsVisible={setIsVisible} />

        {!isMobile && <ToggleArrow isVisible={isVisible} setIsVisible={setIsVisible} />}

        <Content $isMobile={isMobile} $isVisible={isVisible}>
          {isVisible ? (
            <ExpandedLogo src={IMAGES.EXPANDED_LOGO_NAV} />
          ) : (
            <CollapsedLogo src={IMAGES.COLLAPSED_LOGO_NAV} />
          )}
          {!hasDynamicTabs && (
            <NavbarItem
              title="Home"
              href="/"
              isActive={pathname === "/"}
              icon={faHomeAlt}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {!hasDynamicTabs && isManager && (
            <>
              <NavbarItem
                title="Clients"
                href="/clients"
                isActive={pathname.includes("/clients")}
                icon={faUserFriends}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              />
              <NavbarItem
                title="Team Members"
                href="/team-members"
                isActive={pathname.includes("/team-members")}
                icon={faUser}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              />
              <NavbarItem
                title="Vendors"
                href="/vendors"
                isActive={pathname.includes("/vendors")}
                icon={faUserGear}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              />
              {(isTestGroup || isBrookefieldClient) && !hasDynamicTabs && (
                <NavbarItem
                  title="Resources"
                  href="/resources"
                  isActive={pathname.startsWith("/resources")}
                  icon={faUsers}
                  isVisible={isVisible}
                  onClick={() => dispatch(setOpenedClientsPage(1))}
                />
              )}
              <NavbarItem
                title="Forms"
                href="/dynamic-forms"
                isActive={pathname.includes("/dynamic-forms")}
                icon={faFileLines}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              />
              {/* <NavbarItem
                title="KPIs"
                href="/kpi"
                isActive={pathname.includes("/kpi")}
                icon={faChartBar}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              /> */}
            </>
          )}
          {(isTestGroup || isBrookefieldClient) && !hasDynamicTabs && (
            <NavbarItem
              title="PM"
              href="/preventative-maintenance"
              isActive={pathname.includes("/preventative-maintenance")}
              icon={faCalendarDays}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {!isBrookefieldClient && !hasDynamicTabs && (
            <NavbarItem
              title="Files"
              href="/files"
              isActive={pathname.includes("/files")}
              icon={faFolder}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {(isTestGroup || isBrookefieldClient) && !hasDynamicTabs && (
            <NavbarItem
              title="KPIs"
              href="/reporting"
              isActive={pathname.includes("/reporting")}
              icon={faChartMixed}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {(isClient || isTestGroup || isBrookefieldClient) && !hasSchedulesTab && (
            <NavbarItem
              title="Schedules"
              href="/schedules"
              isActive={pathname.includes("/schedules")}
              icon={faCalendarDays}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {clientId !== "OXFORDCOUNTY" && !hasDynamicTabs && (
            <NavbarItem
              title="Work Requests"
              href="/workrequests"
              isActive={pathname.includes("/workrequests")}
              icon={faCheckCircle}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {isManager && clientId !== BROOKEFIELD_CLIENT_ID && (
            <>
              <NavbarItem
                title="Logs"
                href="/washroomlogs"
                isActive={pathname.includes("/washroomlogs")}
                icon={faClock}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedClientsPage(1))}
              />
              {/* <NavbarItem
                title="Assets"
                href="/assets"
                isActive={pathname.includes("/assets")}
                icon={faGear}
                isVisible={isVisible}
                onClick={() => dispatch(setOpenedAssetsPage(1))}
              /> */}
            </>
          )}
          {(isTestGroup || isBrookefieldClient) && !hasDynamicTabs && (
            <NavbarItem
              title="Assets"
              href="/assets"
              isActive={pathname.includes("/assets")}
              icon={faGear}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedAssetsPage(1))}
            />
          )}
          {isManager && !hasDynamicTabs && (
            <NavbarItem
              title="PM"
              href="/preventative-maintenance"
              isActive={pathname.includes("/preventative-maintenance")}
              icon={faCalendarDays}
              isVisible={isVisible}
              onClick={() => dispatch(setOpenedClientsPage(1))}
            />
          )}
          {isClient && clientTabs !== null && (
            <>
              {clientTabs.length > 0 ? (
                <>
                  {!isBrookefieldClient && !hasDynamicTabs && (
                    <NavbarItem
                      title="PM"
                      href="/preventative-maintenance"
                      isActive={pathname.includes("/preventative-maintenance")}
                      icon={faCalendarDays}
                      isVisible={isVisible}
                      onClick={() => dispatch(setOpenedClientsPage(1))}
                    />
                  )}
                  {clientTabs.map((tab) => {
                    const href = tab.route ?? `/${tab.name.toLowerCase().replace(/\s+/g, "-")}`;
                    const basePath = href.split("?")[0];
                    return (
                      <NavbarItem
                        key={tab.id}
                        title={tab.name}
                        href={href}
                        isActive={basePath === "/" ? pathname === "/" : pathname.startsWith(basePath)}
                        icon={ICON_MAP[tab.icon] ?? faHomeAlt}
                        isVisible={isVisible}
                        onClick={() => dispatch(setOpenedClientsPage(1))}
                      />
                    );
                  })}
                </>
              ) : (
                <>
                  {!isBrookefieldClient && !hasDynamicTabs && (
                    <NavbarItem
                      title="Logs"
                      href="/washroomlogs?user=client"
                      isActive={pathname.includes("/washroomlogs") || search.includes("user=client")}
                      icon={faClock}
                      isVisible={isVisible}
                      onClick={() => dispatch(setOpenedClientsPage(1))}
                    />
                  )}
                  {clientId !== "OXFORDCOUNTY" && clientId !== BROOKEFIELD_CLIENT_ID ? (
                    <NavbarItem
                      title="Time & Attendance"
                      href="/time-and-attendance"
                      isActive={pathname.includes("/time-and-attendance")}
                      icon={faMemoCircleCheck}
                      isVisible={isVisible}
                      onClick={() => dispatch(setOpenedClientsPage(1))}
                    />
                  ) : null}
                </>
              )}
            </>
          )}
        </Content>
      </Container>
    </div>
  );
};

export interface ContainerProps {
  $isVisible: boolean;
  $isMobile: boolean;
}

const Container = styled.nav<ContainerProps>`
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  background-color: ${colors.kleenway.backgroundDark};
  border-right: 1px solid ${colors.grey.light11};
  width: ${({ $isVisible }) => ($isVisible ? "250px" : "100px")};
  padding: ${({ $isVisible }) => ($isVisible ? "36px 18px" : "40px 26px")};
  height: ${({ $isVisible, $isMobile }) => ($isMobile ? ($isVisible ? "100%" : "80px") : "100%")};
  position: fixed;
  top: 0;
  z-index: ${({ $isMobile }) => ($isMobile ? 100 : 50)};

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      background-color: ${colors.white.default};
      margin-top: 0;
      padding-bottom: 0;
    `};
`;

const Content = styled.div<ContainerProps>`
  overflow-y: ${({ $isMobile }) => ($isMobile ? "auto" : "initial")};
  display: ${({ $isMobile, $isVisible }) => ($isMobile ? ($isVisible ? "block" : "none") : "block")};
`;

const ExpandedLogo = styled.img`
  width: 200px;
  padding-bottom: 36px;
`;
const CollapsedLogo = styled.img`
  width: 28px;
  padding-bottom: 54px;
  margin: 0 auto;
  display: flex;
`;

export default Navbar;
